import React, { useState, useEffect } from 'react';
import { hospitals } from '../data/mock';
import { MapPin, Award, Bed, Users, ArrowLeft, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const AllHospitals = () => {
  const navigate = useNavigate();
  const [selectedGroup, setSelectedGroup] = useState('All');

  const groups = ['All', 'Max Healthcare', 'Medanta', 'Apollo', 'Fortis', 'Artemis'];
  
  const filteredHospitals = selectedGroup === 'All' 
    ? hospitals 
    : hospitals.filter(h => h.group === selectedGroup);

  return (
    <div>
      {/* SEO Meta Tags with self-referencing canonical */}
      <SEOHead 
        title="Partner Hospitals | JCI Accredited Hospitals in India - Curis Path"
        description="Explore 30+ JCI & NABH accredited partner hospitals across India including Max, Medanta, Apollo, and Fortis. World-class facilities for medical tourists."
        canonical="/hospitals"
      />
      
      <Header />
      
      <section className="section-padding" style={{ background: 'var(--bg-page)', marginTop: '80px' }}>
        <div className="container">
          <div className="mb-8">
            <button 
              onClick={() => navigate('/')}
              className="btn-secondary flex items-center gap-2 mb-6"
            >
              <ArrowLeft size={18} />
              Back to Home
            </button>
            
            <h1 className="heading-hero mb-4">All Partner Hospitals</h1>
            <p className="body-large mb-6" style={{ maxWidth: '800px', color: 'var(--text-secondary)' }}>
              Complete network of {hospitals.length} partner hospitals across India with Max Healthcare, Medanta, Apollo, Fortis, and Artemis groups
            </p>

            {/* Group Filter */}
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <div className="flex items-center gap-2 body-small" style={{ color: 'var(--text-primary)' }}>
                <Filter size={18} />
                <span className="font-semibold">Filter by Group:</span>
              </div>
              {groups.map((group) => (
                <button
                  key={group}
                  onClick={() => setSelectedGroup(group)}
                  className="caption px-4 py-2 rounded-full transition-all"
                  style={{
                    background: selectedGroup === group ? 'var(--text-primary)' : 'rgba(255, 255, 255, 0.6)',
                    color: selectedGroup === group ? 'white' : 'var(--text-primary)',
                    border: '1px solid ' + (selectedGroup === group ? 'var(--text-primary)' : 'rgba(0, 0, 0, 0.1)'),
                    cursor: 'pointer',
                    fontWeight: selectedGroup === group ? '600' : '400'
                  }}
                >
                  {group}
                </button>
              ))}
            </div>

            <div className="inline-block px-4 py-2 rounded-lg mb-4" style={{ background: 'var(--accent-blue-200)' }}>
              <span className="body-small font-semibold">
                Showing {filteredHospitals.length} hospital{filteredHospitals.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredHospitals.map((hospital, index) => {
              const colorClasses = ['accent-blue', 'accent-purple', 'accent-orange', 'accent-pink', 'accent-green', 'accent-grey'];
              const colorClass = colorClasses[index % colorClasses.length];
              
              return (
                <div 
                  key={hospital.id} 
                  className={`voice-card ${colorClass}`}
                >
                  <div className="mb-3 rounded-lg overflow-hidden" style={{ height: '180px' }}>
                    <img 
                      src={hospital.image} 
                      alt={hospital.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    {hospital.featured && (
                      <div 
                        className="inline-block px-2 py-1 rounded-full"
                        style={{ 
                          background: 'var(--text-primary)',
                          color: 'white'
                        }}
                      >
                        <span className="caption font-semibold" style={{ fontSize: '0.65rem' }}>
                          FEATURED
                        </span>
                      </div>
                    )}
                    <div 
                      className="inline-block px-2 py-1 rounded-full"
                      style={{ 
                        background: 'rgba(255, 255, 255, 0.5)',
                        border: '1px solid rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      <span className="caption" style={{ fontSize: '0.65rem', color: 'var(--text-primary)' }}>
                        {hospital.group}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start justify-between mb-2">
                    <h3 className="heading-3 text-base">{hospital.name}</h3>
                    <span 
                      className="caption px-2 py-1 rounded-full flex items-center gap-1 flex-shrink-0 ml-2"
                      style={{ 
                        background: 'rgba(255, 255, 255, 0.6)',
                        border: '1px solid rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      <Award size={10} />
                      {hospital.accreditation.replace(' Accredited', '')}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <MapPin size={14} style={{ color: 'var(--text-muted)' }} />
                    <span className="caption" style={{ color: 'var(--text-secondary)' }}>
                      {hospital.location}
                    </span>
                  </div>

                  <p className="body-small mb-3" style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                    {hospital.description}
                  </p>

                  <div className="grid grid-cols-2 gap-3 mb-3 pt-3" style={{ borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                    <div className="flex items-center gap-2">
                      <Bed size={16} style={{ color: 'var(--text-muted)' }} />
                      <div>
                        <div className="caption font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {hospital.beds}
                        </div>
                        <div className="caption" style={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                          Beds
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users size={16} style={{ color: 'var(--text-muted)' }} />
                      <div>
                        <div className="caption font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {hospital.patientsPerYear}
                        </div>
                        <div className="caption" style={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                          Patients/Year
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1">
                    {hospital.specialties.map((specialty, idx) => (
                      <span 
                        key={idx}
                        className="caption px-2 py-1 rounded-full"
                        style={{ 
                          background: 'rgba(255, 255, 255, 0.5)',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          fontSize: '0.65rem'
                        }}
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {filteredHospitals.length === 0 && (
            <div className="text-center py-12">
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                No hospitals found for the selected group.
              </p>
            </div>
          )}

          <div className="mt-12 text-center">
            <div 
              className="voice-card accent-grey inline-block"
              style={{ padding: '2rem', maxWidth: '600px' }}
            >
              <h3 className="heading-2 mb-3">Ready to Start Your Journey?</h3>
              <p className="body-small mb-4" style={{ color: 'var(--text-secondary)' }}>
                Contact us to learn more about our partner hospitals and find the best fit for your medical needs
              </p>
              <button 
                onClick={() => {
                  navigate('/');
                  setTimeout(() => {
                    document.getElementById('enquiry')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="btn-primary"
              >
                Submit Enquiry
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AllHospitals;
